import { useState } from 'react';
import {
  PortalDsBadge,
  PortalDsButton,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { cn } from 'src/lib/utils';
import type {
  AdminSettableSubscriptionTier,
  PortalUser,
  PortalUserAccessRing,
} from '../types/users.types';
import {
  formatStatusLabel,
  resolvePortalUserStatus,
  resolvePurchasedCreditBalance,
} from '../utils/portal-users.mappers';
import { AccessRingEditor } from './access-ring-editor';
import { AddTokensEditor } from './add-tokens-editor';
import { SubscriptionTierEditor } from './subscription-tier-editor';

const STATUS_VARIANTS = {
  active: 'green',
  blocked: 'red',
} as const;

interface UserManagementPanelProps {
  user: PortalUser;
  disabled?: boolean;
  tierOptions?: string[];
  onSuspend: () => void;
  onReactivate: () => void;
  onBypassEmailVerification: () => void;
  onSetBillingConversionDisabled: (value: boolean) => void;
  onSetConversionModalHidden: (value: boolean) => void;
  onAddTokens: (amount: number) => Promise<{ ok: boolean; message?: string }>;
  onSaveSubscriptionTier: (
    workspaceId: string,
    tier: AdminSettableSubscriptionTier
  ) => Promise<{ ok: boolean; message?: string }>;
  onSetAccessRing: (
    accessRing: PortalUserAccessRing
  ) => Promise<{ ok: boolean; message?: string }>;
}

export function UserManagementPanel({
  user,
  disabled = false,
  tierOptions,
  onSuspend,
  onReactivate,
  onBypassEmailVerification,
  onSetBillingConversionDisabled,
  onSetConversionModalHidden,
  onAddTokens,
  onSaveSubscriptionTier,
  onSetAccessRing,
}: UserManagementPanelProps) {
  const [billingConfirmOpen, setBillingConfirmOpen] = useState(false);
  const [pendingBillingValue, setPendingBillingValue] = useState<boolean | null>(null);
  const [emailBypassConfirmOpen, setEmailBypassConfirmOpen] = useState(false);
  const [suspendConfirmOpen, setSuspendConfirmOpen] = useState(false);

  const status = resolvePortalUserStatus(user);
  const isBlocked = status === 'blocked';

  const billingConfirmMessage = pendingBillingValue
    ? 'This exempts the user from all billing limits. Continue?'
    : 'This removes the billing exemption and restores tiered billing limits. Continue?';

  const handleBillingConfirm = () => {
    if (pendingBillingValue === null) {
      return;
    }

    onSetBillingConversionDisabled(pendingBillingValue);
    setBillingConfirmOpen(false);
    setPendingBillingValue(null);
  };

  const handleEmailBypassConfirm = () => {
    onBypassEmailVerification();
    setEmailBypassConfirmOpen(false);
  };

  const handleSuspendConfirm = () => {
    onSuspend();
    setSuspendConfirmOpen(false);
  };

  return (
    <div className="space-y-8">
      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Subscription tier
        </h3>
        <SubscriptionTierEditor
          workspaceId={user.workspace_id}
          tier={user.subscription}
          tierOptions={tierOptions}
          disabled={disabled}
          onSave={onSaveSubscriptionTier}
        />
      </section>

      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Early Access
        </h3>
        <AccessRingEditor
          accessRing={user.access_ring}
          disabled={disabled}
          onChange={onSetAccessRing}
        />
      </section>

      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Creative generation tokens
        </h3>
        <p className={cn(portalDs.bodySm, 'mb-3')}>
          Top up purchased creative-generation tokens for this workspace.
        </p>
        <AddTokensEditor
          currentPurchasedBalance={resolvePurchasedCreditBalance(user)}
          disabled={disabled || !user.workspace_id}
          onAdd={onAddTokens}
        />
      </section>

      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Account actions
        </h3>
        <div className="mb-3 flex items-center gap-2">
          <span className={cn(portalDs.bodySm, 'text-[#5E6278]')}>
            Account status
          </span>
          <PortalDsBadge
            variant={STATUS_VARIANTS[status]}
            pill
          >
            {formatStatusLabel(status)}
          </PortalDsBadge>
        </div>
        <div className="flex flex-wrap gap-2">
          {isBlocked ? (
            <PortalDsButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={onReactivate}
            >
              Reactivate
            </PortalDsButton>
          ) : (
            <PortalDsButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              className="border-[#FECACA] text-[#DC2626] hover:bg-[#FEF2F2]"
              onClick={() => setSuspendConfirmOpen(true)}
            >
              Suspend account
            </PortalDsButton>
          )}
          {!user.email_verified ? (
            <PortalDsButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={() => setEmailBypassConfirmOpen(true)}
            >
              Bypass email verification
            </PortalDsButton>
          ) : null}
        </div>
      </section>

      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Billing
        </h3>
        <p className={cn(portalDs.bodySm, 'mb-3')}>
          Exemption removes limits; hiding prompts only changes paywall messaging.
        </p>
        <div className="flex flex-wrap gap-2">
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => {
              setPendingBillingValue(!user.disable_billing_conversion);
              setBillingConfirmOpen(true);
            }}
          >
            {user.disable_billing_conversion
              ? 'Remove billing exemption'
              : 'Exempt from billing limits'}
          </PortalDsButton>
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => onSetConversionModalHidden(!user.hide_conversion_modal)}
          >
            {user.hide_conversion_modal
              ? 'Show upgrade prompts'
              : 'Hide upgrade prompts'}
          </PortalDsButton>
        </div>
      </section>

      <Dialog
        open={suspendConfirmOpen}
        onOpenChange={setSuspendConfirmOpen}
      >
        <DialogContent className="max-w-md border-[#EDEEF2] bg-white p-0 shadow-[0_4px_16px_rgba(13,15,26,0.10)]">
          <DialogHeader className="border-b border-[#EDEEF2] px-5 py-4 text-left">
            <DialogTitle className="text-[15px] font-semibold text-[#0D0F1A]">
              Suspend account
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[#5E6278]">
              This blocks the user from signing in and using the platform.
              Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 px-5 py-4 sm:justify-end">
            <PortalDsButton
              variant="secondary"
              size="sm"
              onClick={() => setSuspendConfirmOpen(false)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              variant="secondary"
              size="sm"
              className="border-[#FECACA] bg-[#DC2626] text-white hover:bg-[#B91C1C]"
              onClick={handleSuspendConfirm}
            >
              Suspend account
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={billingConfirmOpen}
        onOpenChange={(nextOpen) => {
          setBillingConfirmOpen(nextOpen);
          if (!nextOpen) {
            setPendingBillingValue(null);
          }
        }}
      >
        <DialogContent className="max-w-md border-[#EDEEF2] bg-white p-0 shadow-[0_4px_16px_rgba(13,15,26,0.10)]">
          <DialogHeader className="border-b border-[#EDEEF2] px-5 py-4 text-left">
            <DialogTitle className="text-[15px] font-semibold text-[#0D0F1A]">
              Confirm billing exemption change
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[#5E6278]">
              {billingConfirmMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 px-5 py-4 sm:justify-end">
            <PortalDsButton
              variant="secondary"
              size="sm"
              onClick={() => {
                setBillingConfirmOpen(false);
                setPendingBillingValue(null);
              }}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              variant="primary"
              size="sm"
              onClick={handleBillingConfirm}
            >
              Continue
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={emailBypassConfirmOpen}
        onOpenChange={setEmailBypassConfirmOpen}
      >
        <DialogContent className="max-w-md border-[#EDEEF2] bg-white p-0 shadow-[0_4px_16px_rgba(13,15,26,0.10)]">
          <DialogHeader className="border-b border-[#EDEEF2] px-5 py-4 text-left">
            <DialogTitle className="text-[15px] font-semibold text-[#0D0F1A]">
              Bypass email verification
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[#5E6278]">
              This marks the user&apos;s email as verified without them clicking the link.
              Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 px-5 py-4 sm:justify-end">
            <PortalDsButton
              variant="secondary"
              size="sm"
              onClick={() => setEmailBypassConfirmOpen(false)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              variant="primary"
              size="sm"
              onClick={handleEmailBypassConfirm}
            >
              Continue
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
